import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, 
  Image as ImageIcon, 
  Eye, 
  Clock, 
  Lock, 
  ArrowSquareOut,
  FunnelSimple,
  X,
  ArrowLeft,
  ArrowRight,
  Heart,
  ShareNetwork,
  DownloadSimple,
  Fire,
  TrendUp,
  User,
  Coins,
  Star,
  Crown,
} from '@phosphor-icons/react';
import { useTranslation } from '../context/LanguageContext';
import HorizontalScroller from '../components/common/HorizontalScroller';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const proxyImageUrl = (url) => {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    const proxyDomains = ['preview.redd.it', 'external-preview.redd.it', 'i.redd.it', 'i.redgifs.com', 'thumbs2.redgifs.com', 'thumbs3.redgifs.com', 'thumbs4.redgifs.com', 'files.mastodon.social', 'media.mastodon.social'];
    if (proxyDomains.some(d => parsed.hostname.endsWith(d))) {
      return `${API}/api/proxy/image?url=${encodeURIComponent(url)}`;
    }
  } catch {}
  return url;
};

const getBestImageUrl = (item) => {
  const contentUrl = item.content_url || '';
  const thumbUrl = item.thumbnail_url || '';
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  if (contentUrl && imageExts.some(ext => contentUrl.toLowerCase().includes(ext))) {
    return proxyImageUrl(contentUrl);
  }
  return proxyImageUrl(thumbUrl);
};

const ITEMS_PER_PAGE = 24;

const BrowsePage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' | 'trending'
  const [content, setContent] = useState([]);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    category: '',
    tier: '',
    content_type: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  // Trending state
  const [trendingItems, setTrendingItems] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  const [trendingTimeframe, setTrendingTimeframe] = useState('week');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/external/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  }, []);

  const fetchContent = useCallback(async (append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const skip = append ? content.length : 0;
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.tier) params.append('tier', filters.tier);
      if (filters.content_type) params.append('content_type', filters.content_type);
      params.append('skip', skip.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      
      const response = await axios.get(
        `${API}/api/external/browse?${params.toString()}`,
        { withCredentials: true }
      );
      const newItems = response.data.items;
      const totalCount = response.data.total || 0;
      
      if (append) {
        setContent(prev => [...prev, ...newItems]);
      } else {
        setContent(newItems);
      }
      setTotal(totalCount);
      setHasMore((append ? content.length + newItems.length : newItems.length) < totalCount);
    } catch (err) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters, content.length]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchContent(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Trending fetch
  const fetchTrending = useCallback(async () => {
    setTrendingLoading(true);
    try {
      const res = await axios.get(`${API}/api/external/trending?limit=48&timeframe=${trendingTimeframe}`, { withCredentials: true });
      setTrendingItems(res.data.items || []);
    } catch {
      toast.error('Failed to load trending');
    } finally {
      setTrendingLoading(false);
    }
  }, [trendingTimeframe]);

  useEffect(() => {
    if (activeTab === 'trending') {
      fetchTrending();
    }
  }, [activeTab, fetchTrending]);

  const openLightbox = (item, index) => {
    if (!item.is_free) {
      toast.info('Subscribe to access premium content');
      return;
    }
    setLightbox(item);
    setLightboxIndex(index);
  };

  const navigateLightbox = (direction) => {
    const sourceItems = activeTab === 'trending'
      ? trendingItems.filter(c => c.is_free !== false)
      : content.filter(c => c.is_free);
    const currentIdx = sourceItems.findIndex(c => c.id === lightbox?.id);
    const newIndex = currentIdx + direction;
    if (newIndex >= 0 && newIndex < sourceItems.length) {
      setLightbox(sourceItems[newIndex]);
    }
  };

  const closeLightbox = () => {
    setLightbox(null);
    setLightboxIndex(-1);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (!lightbox) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const clearFilters = () => {
    setFilters({ category: '', tier: '', content_type: '' });
  };

  const hasActiveFilters = filters.category || filters.tier || filters.content_type;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 bg-slate-900/50 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
              activeTab === 'browse'
                ? 'bg-white/10 text-white'
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid="tab-browse"
          >
            {t('browse_title')}
          </button>
          <button
            onClick={() => setActiveTab('trending')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
              activeTab === 'trending'
                ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-orange-300 ring-1 ring-orange-500/30'
                : 'text-slate-400 hover:text-orange-300'
            }`}
            data-testid="tab-trending"
          >
            <Fire weight="fill" className="w-4 h-4" />
            {t('browse_trending')}
          </button>
        </div>

        {activeTab === 'browse' ? (
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
            data-testid="toggle-filters-btn"
          >
            <FunnelSimple className="w-5 h-5" />
            Filters
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
        ) : (
          <div className="flex items-center gap-1 bg-slate-900/50 rounded-lg p-0.5">
            {[
              { value: 'day', label: '24h' },
              { value: 'week', label: '7d' },
              { value: 'month', label: '30d' },
            ].map(tf => (
              <button
                key={tf.value}
                onClick={() => setTrendingTimeframe(tf.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  trendingTimeframe === tf.value
                    ? 'bg-orange-500/20 text-orange-300'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid={`trending-tf-${tf.value}`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════ TRENDING TAB ═══════════ */}
      {activeTab === 'trending' && (
        <div>
          {trendingLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : trendingItems.length === 0 ? (
            <div className="text-center py-20">
              <Fire className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">{t('browse_no_trending')}</h2>
              <p className="text-slate-500">Check back later for trending content</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Top 3 Hero */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {trendingItems.slice(0, 3).map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="group relative rounded-2xl overflow-hidden bg-slate-900 cursor-pointer ring-1 ring-orange-500/20"
                    onClick={() => openLightbox(item, idx)}
                    data-testid={`trending-hero-${idx}`}
                  >
                    <div className="relative aspect-[3/4]">
                      <img
                        src={getBestImageUrl(item)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => { e.target.closest('.group').style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

                      {/* Rank Badge */}
                      <div className="absolute top-3 left-3">
                        <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                          idx === 0 ? 'bg-amber-400 text-black' : idx === 1 ? 'bg-slate-300 text-black' : 'bg-orange-600 text-white'
                        }`}>
                          {idx === 0 ? <Crown weight="fill" className="w-3.5 h-3.5" /> : <TrendUp weight="bold" className="w-3.5 h-3.5" />}
                          #{idx + 1}
                        </span>
                      </div>

                      {/* Source badge */}
                      {item.trend_source === 'creator' && (
                        <div className="absolute top-3 right-3">
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/80 text-white rounded-full text-xs font-medium">
                            <Star weight="fill" className="w-3 h-3" />
                            Creator
                          </span>
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-bold text-base line-clamp-2 mb-2">{item.title}</h3>
                        <div className="flex items-center justify-between text-xs text-slate-300">
                          <span className="flex items-center gap-1 truncate">
                            {item.trend_source === 'creator' ? <User className="w-3 h-3" /> : null}
                            {item.source_name || item.creator_name || 'Reddit'}
                          </span>
                          <span className="flex items-center gap-2">
                            <span className="flex items-center gap-0.5">
                              <Fire weight="fill" className="w-3 h-3 text-orange-400" />
                              {item.trend_score}
                            </span>
                            {item.views > 0 && (
                              <span className="flex items-center gap-0.5">
                                <Eye className="w-3 h-3" /> {item.views}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Rest of trending */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {trendingItems.slice(3).map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min((idx + 3) * 0.03, 0.5) }}
                    className="group relative rounded-2xl overflow-hidden bg-slate-900 cursor-pointer"
                    onClick={() => openLightbox(item, idx + 3)}
                    data-testid={`trending-item-${idx + 3}`}
                  >
                    <div className="relative aspect-[3/4]">
                      <img
                        src={getBestImageUrl(item)}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => { e.target.closest('.group').style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 text-xs font-bold">
                          <TrendUp weight="bold" className="w-3 h-3" />
                          #{idx + 4}
                        </span>
                        {item.trend_source === 'creator' && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-500/60 text-white rounded-full text-[10px] font-medium">
                            <Star weight="fill" className="w-2.5 h-2.5" />
                          </span>
                        )}
                      </div>

                      {item.content_type === 'video' && (
                        <div className="absolute top-3 right-3">
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/80 text-white rounded-full text-xs">
                            <Play weight="fill" className="w-3 h-3" />
                          </span>
                        </div>
                      )}

                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-white font-medium text-sm line-clamp-2 mb-1">{item.title}</h3>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span className="truncate">{item.source_name || item.creator_name || 'Reddit'}</span>
                          <span className="flex items-center gap-0.5">
                            <Fire weight="fill" className="w-3 h-3 text-orange-400" />
                            {item.trend_score}
                          </span>
                        </div>
                      </div>

                      <div className="absolute inset-0 bg-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Eye className="w-7 h-7 text-white" />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ BROWSE TAB ═══════════ */}
      {activeTab === 'browse' && (<>

      {/* Filters Panel */}
      {showFilters && categories && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-6 p-4 glass-light rounded-2xl"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium">Filter Content</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Category</label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white"
                data-testid="category-filter"
              >
                <option value="">All Categories</option>
                {categories.categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name} ({cat.count})</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Access Level</label>
              <select
                value={filters.tier}
                onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white"
                data-testid="tier-filter"
              >
                <option value="">All Tiers</option>
                {categories.tiers.map(tier => (
                  <option key={tier.id} value={tier.id}>{tier.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-2">Type</label>
              <select
                value={filters.content_type}
                onChange={(e) => setFilters({ ...filters, content_type: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white"
                data-testid="type-filter"
              >
                <option value="">All Types</option>
                {categories.content_types.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>
      )}

      {/* Category Pills */}
      {categories && (
        <HorizontalScroller className="mb-6" data-testid="browse-category-strip">
          <button
            onClick={() => setFilters({ ...filters, category: '' })}
            className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
              !filters.category
                ? 'bg-blue-500 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            All
          </button>
          {categories.categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setFilters({ ...filters, category: cat.id })}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors flex-shrink-0 ${
                filters.category === cat.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </HorizontalScroller>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Content Grid */}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {content.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.03, 0.5) }}
              className="group relative rounded-2xl overflow-hidden bg-slate-900 cursor-pointer"
              onClick={() => openLightbox(item, index)}
              data-testid={`external-content-${item.id}`}
            >
              <div className="relative aspect-[3/4]">
                <img
                  src={getBestImageUrl(item)}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { e.target.closest('.group').style.display = 'none'; }}
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                {/* Content Type Badge */}
                <div className="absolute top-3 left-3">
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    item.content_type === 'video'
                      ? 'bg-blue-500/80 text-white'
                      : 'bg-amber-500/80 text-amber-950'
                  }`}>
                    {item.content_type === 'video' ? (
                      <Play weight="fill" className="w-3 h-3" />
                    ) : (
                      <ImageIcon className="w-3 h-3" />
                    )}
                    {item.content_type === 'video' ? 'Video' : 'Gallery'}
                  </span>
                </div>
                
                {/* Tier Badge */}
                {!item.is_free && (
                  <div className="absolute top-3 right-3">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      item.tier === 'premium'
                        ? 'bg-amber-500 text-amber-950'
                        : 'bg-slate-700 text-white'
                    }`}>
                      <Lock weight="fill" className="w-3 h-3" />
                      {item.tier}
                    </span>
                  </div>
                )}
                
                {/* Duration */}
                {item.duration && (
                  <div className="absolute bottom-12 right-3">
                    <span className="flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded text-xs text-white">
                      <Clock className="w-3 h-3" />
                      {item.duration}
                    </span>
                  </div>
                )}
                
                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <h3 className="text-white font-medium text-sm line-clamp-2 mb-1">
                    {item.title}
                  </h3>
                  <div className="flex items-center justify-between text-xs text-slate-400">
                    <span className="truncate">{item.source_name || 'Reddit'}</span>
                    {item.views > 0 && (
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {(item.views / 1000).toFixed(0)}K
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {item.is_free ? (
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Eye className="w-7 h-7 text-white" />
                    </div>
                  ) : (
                    <div className="text-center">
                      <Lock className="w-8 h-8 text-white mx-auto mb-2" />
                      <span className="text-white text-sm font-medium">Subscribe to access</span>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Load More */}
      {!loading && hasMore && content.length > 0 && (
        <div className="flex justify-center mt-8">
          <button
            onClick={() => fetchContent(true)}
            disabled={loadingMore}
            className="px-8 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 text-white rounded-full font-medium transition-colors flex items-center gap-2"
            data-testid="load-more-btn"
          >
            {loadingMore ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Loading...
              </>
            ) : (
              <>Load More ({content.length} of {total})</>
            )}
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && content.length === 0 && (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No content found</h2>
          <p className="text-slate-500 mb-4">Try adjusting your filters</p>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-blue-500 text-white rounded-full font-medium hover:bg-blue-600 transition-colors"
            data-testid="clear-filters-btn"
          >
            Clear Filters
          </button>
        </div>
      )}

      </>)}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox-overlay"
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="lightbox-close-btn"
            >
              <X weight="bold" className="w-5 h-5" />
            </button>

            {/* Nav Arrows */}
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="lightbox-prev-btn"
            >
              <ArrowLeft weight="bold" className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              data-testid="lightbox-next-btn"
            >
              <ArrowRight weight="bold" className="w-6 h-6" />
            </button>

            {/* Image */}
            <div
              className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.img
                key={lightbox.id}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                src={getBestImageUrl(lightbox)}
                alt={lightbox.title}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
                data-testid="lightbox-image"
              />
              
              {/* Caption */}
              <div className="mt-4 text-center max-w-lg">
                <h3 className="text-white font-medium text-lg mb-1">{lightbox.title}</h3>
                <div className="flex items-center justify-center gap-4 text-sm text-slate-400">
                  <span>{lightbox.source_name || 'Reddit'}</span>
                  {lightbox.views > 0 && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {(lightbox.views / 1000).toFixed(0)}K views
                    </span>
                  )}
                  {lightbox.tags && lightbox.tags.length > 0 && (
                    <span className="text-blue-400">
                      {lightbox.tags.slice(0, 3).join(', ')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info Banner */}
      <div className="mt-8 p-4 glass-light rounded-2xl">
        <div className="flex items-start gap-3">
          <Eye className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-400">
            <p className="mb-1">
              <span className="text-white font-medium">Free Content:</span> Browse and view content directly on LustLink. 
              Images are displayed inline — no external accounts needed.
            </p>
            <p>
              Premium and Basic tier content is available as bonus content with subscriptions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrowsePage;
